import { useEffect, useState } from 'react';
import { Wifi, WifiOff, Zap, Activity } from 'lucide-react';

interface LoadingScreenProps {
  /** Message personnalisé */
  message?: string;
  /** Afficher la progression */
  showProgress?: boolean;
  /** Progression actuelle (0-100) */
  progress?: number;
  /** Étapes de chargement */
  steps?: LoadingStep[];
  /** Qualité de connexion */
  connectionQuality?: 'excellent' | 'good' | 'moderate' | 'slow' | 'offline';
}

interface LoadingStep {
  id: string;
  label: string;
  status: 'pending' | 'loading' | 'completed' | 'error';
}

/**
 * Écran de chargement amélioré style YouTube/moderne
 * - Progression visuelle
 * - Adaptation à la connexion
 * - Animation fluide
 */
export function LoadingScreen({ 
  message,
  showProgress = true,
  progress = 0,
  steps,
  connectionQuality = 'good'
}: LoadingScreenProps) {
  const [displayProgress, setDisplayProgress] = useState(0);
  const [currentTip, setCurrentTip] = useState(0);

  // Tips rotatifs pendant le chargement
  const tips = [
    'Préparation de votre espace de travail...',
    'Chargement des données comptables...',
    'Synchronisation avec le serveur...',
    'Optimisation de l\'interface...',
    'Presque prêt...'
  ];

  // Animation fluide de la progression
  useEffect(() => {
    const timer = setInterval(() => {
      setDisplayProgress(prev => {
        const diff = progress - prev;
        if (Math.abs(diff) < 1) return progress;
        return prev + diff * 0.1;
      });
    }, 50);
    return () => clearInterval(timer);
  }, [progress]);

  // Rotation des tips
  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentTip(prev => (prev + 1) % tips.length);
    }, 3000);
    return () => clearInterval(interval);
  }, [tips.length]);

  // Icône de connexion
  const ConnectionIcon = () => {
    switch (connectionQuality) {
      case 'offline':
        return <WifiOff className="h-4 w-4 text-destructive" />;
      case 'slow':
        return <Wifi className="h-4 w-4 text-warning" />;
      case 'excellent':
        return <Zap className="h-4 w-4 text-success" />;
      default:
        return <Activity className="h-4 w-4 text-primary" />;
    }
  };

  return (
    <div className="fixed inset-0 bg-background z-50 flex flex-col">
      {/* Barre de progression top (style YouTube) */}
      {showProgress && (
        <div className="absolute top-0 left-0 right-0 h-1 bg-muted overflow-hidden">
          <div 
            className="h-full bg-primary transition-all duration-300 ease-out"
            style={{ width: `${displayProgress}%` }}
          />
          {/* Animation de brillance */}
          <div 
            className="absolute top-0 h-full w-32 bg-gradient-to-r from-transparent via-white/30 to-transparent animate-shimmer-fast"
            style={{ left: `${displayProgress - 20}%` }}
          />
        </div>
      )}

      {/* Contenu central */}
      <div className="flex-1 flex items-center justify-center">
        <div className="text-center max-w-md px-6">
          {/* Logo animé */}
          <div className="relative mb-8">
            <div className="w-20 h-20 mx-auto relative">
              {/* Cercle externe animé */}
              <svg className="absolute inset-0 w-full h-full animate-spin-slow" viewBox="0 0 100 100">
                <circle
                  cx="50"
                  cy="50"
                  r="45"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  className="text-muted"
                />
                <circle
                  cx="50"
                  cy="50"
                  r="45"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeDasharray={`${displayProgress * 2.83} 283`}
                  className="text-primary transition-all duration-300"
                  transform="rotate(-90 50 50)"
                />
              </svg>
              
              {/* Logo/Icône central */}
              <div className="absolute inset-0 flex items-center justify-center">
                <div className="w-12 h-12 bg-primary/10 rounded-xl flex items-center justify-center">
                  <span className="text-2xl font-bold text-primary">W</span>
                </div>
              </div>
            </div>
            
            {/* Pourcentage */}
            {showProgress && (
              <div className="mt-4 text-2xl font-semibold text-foreground">
                {Math.round(displayProgress)}%
              </div>
            )}
          </div>

          {/* Titre */}
          <h1 className="text-xl font-semibold text-foreground mb-2">
            Wanzo Compta
          </h1>

          {/* Message/Tip rotatif */}
          <p className="text-muted-foreground text-sm mb-6 h-5 transition-opacity duration-500">
            {message || tips[currentTip]}
          </p>

          {/* Étapes de chargement */}
          {steps && steps.length > 0 && (
            <div className="space-y-2 text-left bg-muted/30 rounded-lg p-4 mb-6">
              {steps.map((step) => (
                <div key={step.id} className="flex items-center gap-3">
                  {/* Indicateur de statut */}
                  <div className="w-5 h-5 flex items-center justify-center">
                    {step.status === 'completed' && (
                      <div className="w-4 h-4 bg-success rounded-full flex items-center justify-center">
                        <svg className="w-3 h-3 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                        </svg>
                      </div>
                    )}
                    {step.status === 'loading' && (
                      <div className="w-4 h-4 border-2 border-primary border-t-transparent rounded-full animate-spin" />
                    )}
                    {step.status === 'pending' && (
                      <div className="w-3 h-3 bg-muted-foreground/30 rounded-full" />
                    )}
                    {step.status === 'error' && (
                      <div className="w-4 h-4 bg-destructive rounded-full flex items-center justify-center">
                        <span className="text-white text-xs">!</span>
                      </div>
                    )}
                  </div>
                  
                  {/* Label */}
                  <span className={`text-sm ${
                    step.status === 'completed' ? 'text-muted-foreground' :
                    step.status === 'loading' ? 'text-foreground font-medium' :
                    step.status === 'error' ? 'text-destructive' :
                    'text-muted-foreground/60'
                  }`}>
                    {step.label}
                  </span>
                </div>
              ))}
            </div>
          )}

          {/* Indicateur de connexion */}
          <div className="flex items-center justify-center gap-2 text-xs text-muted-foreground">
            <ConnectionIcon />
            <span>
              {connectionQuality === 'offline' && 'Mode hors-ligne'}
              {connectionQuality === 'slow' && 'Connexion lente détectée'}
              {connectionQuality === 'moderate' && 'Connexion modérée'}
              {connectionQuality === 'good' && 'Bonne connexion'}
              {connectionQuality === 'excellent' && 'Connexion excellente'}
            </span>
          </div>
        </div>
      </div>

      {/* Footer */}
      <div className="p-4 text-center">
        <p className="text-xs text-muted-foreground">
          © {new Date().getFullYear()} Wanzo Compta - Solution comptable intelligente
        </p>
      </div>
    </div>
  );
}

/**
 * Composant de progression inline (pour les composants individuels)
 */
export function InlineLoader({ 
  size = 'md',
  className = '' 
}: { 
  size?: 'sm' | 'md' | 'lg';
  className?: string;
}) {
  const sizeClasses = {
    sm: 'h-4 w-4',
    md: 'h-6 w-6',
    lg: 'h-8 w-8'
  };

  return (
    <div className={`flex items-center justify-center ${className}`}>
      <div className={`${sizeClasses[size]} border-2 border-primary border-t-transparent rounded-full animate-spin`} />
    </div>
  );
}

/**
 * Overlay de chargement pour les composants
 */
export function LoadingOverlay({ 
  message,
  transparent = false 
}: { 
  message?: string;
  transparent?: boolean;
}) {
  return (
    <div className={`absolute inset-0 flex items-center justify-center z-10 ${
      transparent ? 'bg-background/50' : 'bg-background/80'
    } backdrop-blur-sm rounded-lg`}>
      <div className="text-center">
        <InlineLoader size="lg" className="mb-2" />
        {message && (
          <p className="text-sm text-muted-foreground">{message}</p>
        )}
      </div>
    </div>
  );
}
